import React, { useEffect, useRef } from "react";
import { drawSpawnAnim, SPAWN_ANIMS } from "../../game-canvas/spawn-anim";

// Dev-only gallery of worm SPAWN-IN animations (route: /spawn). Each tile loops:
// empty board -> the worm spawns in -> brief idle -> repeat. In game these play
// staggered across worms during the round countdown.
const TILE_W = 300;
const TILE_H = 150;
const HEAD_R = 9;
const COLORS = ["#4aa3ff", "#ff5c7a", "#7CFC66"];

const T_EMPTY = 0.35, T_SPAWN = 0.55, T_HOLD = 1.0;
const TOTAL = T_EMPTY + T_SPAWN + T_HOLD;

export default function SpawnAnimDemo() {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvasRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });

    // The worm as the host would draw it: a short curved body stub + orange head,
    // honouring the design's scale/alpha/offset.
    function wormDrawer(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
      return (opts?: { scale?: number; alpha?: number; dx?: number; dy?: number }) => {
        const s = opts?.scale ?? 1, a = opts?.alpha ?? 1, dx = opts?.dx ?? 0, dy = opts?.dy ?? 0;
        if (s <= 0.01 || a <= 0.01) return;
        ctx.save();
        ctx.globalAlpha = a;
        ctx.translate(x + dx, y + dy);
        ctx.scale(s, s);
        ctx.strokeStyle = color;
        ctx.lineWidth = HEAD_R * 1.6;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(-8, 4);
        ctx.quadraticCurveTo(-30, 14, -52, 6);
        ctx.stroke();
        ctx.fillStyle = "#FFB74D";
        ctx.beginPath(); ctx.arc(0, 0, HEAD_R * 0.8, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      };
    }

    function loop(now: number) {
      if (!startRef.current) startRef.current = now;
      const t = (now - startRef.current) / 1000;
      SPAWN_ANIMS.forEach((v, idx) => {
        const c = canvasRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        // Three staggered worms per tile, like the countdown staggers spawns in game.
        COLORS.forEach((color, wi) => {
          const x = 80 + wi * 70, y = 55 + (wi % 2) * 45;
          const lt = (t + idx * 0.17) % TOTAL;
          const wlt = lt - T_EMPTY - wi * 0.22; // per-worm stagger
          const p = wlt <= 0 ? 0 : Math.min(1, wlt / T_SPAWN);
          if (p <= 0) return;
          drawSpawnAnim(ctx, x, y, HEAD_R, color, p, t, v.n, wormDrawer(ctx, x, y, color));
        });
      });
      rafRef.current = window.requestAnimationFrame(loop);
    }
    rafRef.current = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{ background: "#05070d", minHeight: "100vh", padding: 24, color: "#eaf0ff", fontFamily: "'Rajdhani',sans-serif" }}>
      <h1 style={{ fontFamily: "'Orbitron',sans-serif", fontSize: 20, letterSpacing: 2 }}>WORM SPAWN ANIMATIONS</h1>
      <p style={{ color: "#8995b3", maxWidth: 720 }}>
        Each tile loops three staggered spawns, like worms popping onto the board one after
        another during the round countdown. #1 is the proposed basic default.
      </p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18 }}>
        {SPAWN_ANIMS.map((v, idx) => (
          <div key={v.n} style={{ width: TILE_W }}>
            <canvas
              ref={(el) => { canvasRefs.current[idx] = el; }}
              style={{ width: TILE_W, height: TILE_H, background: "#0b0f1a", border: "1px solid #1e2636", borderRadius: 8 }}
            />
            <div style={{ fontSize: 13, color: "#cbd3e6", marginTop: 4 }}>#{v.n} {v.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
